import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { adminApi, adminSettingsApi } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Card } from "@profitlens/ui/card";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@profitlens/ui/tabs";

import { PageHeader } from "@/components/page-header";
import { Pagination } from "@/components/pagination";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 20;
const ALL_TENANTS = "__all__";

export function DataPage() {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState<string>(ALL_TENANTS);

  const tenants = useQuery({
    queryKey: ["admin", "tenants", "options"],
    queryFn: () => adminApi.listTenants({ page: 1, page_size: 100 }),
    staleTime: 60_000,
  });

  const tenantFilter = tenantId === ALL_TENANTS ? undefined : tenantId;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("data.title")}
        subtitle={t("data.subtitle")}
        actions={
          <Select value={tenantId} onValueChange={setTenantId}>
            <SelectTrigger className="w-56" aria-label={t("data.tenantFilter")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TENANTS}>{t("common.all")}</SelectItem>
              {tenants.data?.items.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <Tabs defaultValue="stores">
        <TabsList>
          <TabsTrigger value="stores">{t("data.tabStores")}</TabsTrigger>
          <TabsTrigger value="products">{t("data.tabProducts")}</TabsTrigger>
          <TabsTrigger value="orders">{t("data.tabOrders")}</TabsTrigger>
          <TabsTrigger value="customers">{t("data.tabCustomers")}</TabsTrigger>
        </TabsList>
        <TabsContent value="stores">
          <StoresTab tenantId={tenantFilter} />
        </TabsContent>
        <TabsContent value="products">
          <ProductsTab tenantId={tenantFilter} />
        </TabsContent>
        <TabsContent value="orders">
          <OrdersTab tenantId={tenantFilter} />
        </TabsContent>
        <TabsContent value="customers">
          <CustomersTab tenantId={tenantFilter} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StoresTab({ tenantId }: { tenantId?: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["admin", "data", "stores", { tenantId, q, page }],
    queryFn: () =>
      adminSettingsApi.dataStores({
        tenant_id: tenantId,
        q: q || undefined,
        page,
        page_size: PAGE_SIZE,
      }),
  });

  return (
    <TabShell
      searchValue={q}
      onSearch={(v) => {
        setQ(v);
        setPage(1);
      }}
      searchPlaceholder={t("data.searchStores")}
      isLoading={list.isLoading}
      error={list.error ?? null}
      onRetry={() => list.refetch()}
      isEmpty={!!list.data && list.data.items.length === 0}
      emptyText={t("data.empty")}
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onPage={setPage}
        />
      }
    >
      <TableHeader>
        <TableRow>
          <TableHead>{t("data.colTenant")}</TableHead>
          <TableHead>{t("data.colPlatform")}</TableHead>
          <TableHead>{t("data.colDomain")}</TableHead>
          <TableHead>{t("data.colStatus")}</TableHead>
          <TableHead>{t("data.colSync")}</TableHead>
          <TableHead>{t("data.colLastSync")}</TableHead>
          <TableHead>{t("data.colProducts")}</TableHead>
          <TableHead>{t("data.colCreatedAt")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.data?.items.map((s) => (
          <TableRow key={s.id}>
            <TableCell className="font-medium">{s.tenant_name ?? "—"}</TableCell>
            <TableCell>
              {s.platform === "woocommerce" ? t("data.platformWoo") : t("data.platformShopify")}
            </TableCell>
            <TableCell className="text-xs">{s.domain}</TableCell>
            <TableCell>
              <StoreStatusBadge status={s.status} />
            </TableCell>
            <TableCell>{s.sync_enabled ? t("common.yes") : t("common.no")}</TableCell>
            <TableCell className="text-xs whitespace-nowrap text-muted">
              {s.last_synced_at ? formatDateTime(s.last_synced_at) : t("common.never")}
            </TableCell>
            <TableCell className="tabular-nums">{s.products}</TableCell>
            <TableCell className="text-xs whitespace-nowrap text-muted">
              {formatDateTime(s.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TabShell>
  );
}

function StoreStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  if (status === "connected") return <Badge variant="success">{t("data.storeConnected")}</Badge>;
  if (status === "error") return <Badge variant="danger">{t("data.storeError")}</Badge>;
  return <Badge variant="secondary">{t("data.storeDisconnected")}</Badge>;
}

function ProductsTab({ tenantId }: { tenantId?: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["admin", "data", "products", { tenantId, q, page }],
    queryFn: () =>
      adminSettingsApi.dataProducts({
        tenant_id: tenantId,
        q: q || undefined,
        page,
        page_size: PAGE_SIZE,
      }),
  });

  return (
    <TabShell
      searchValue={q}
      onSearch={(v) => {
        setQ(v);
        setPage(1);
      }}
      searchPlaceholder={t("data.searchProducts")}
      isLoading={list.isLoading}
      error={list.error ?? null}
      onRetry={() => list.refetch()}
      isEmpty={!!list.data && list.data.items.length === 0}
      emptyText={t("data.empty")}
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onPage={setPage}
        />
      }
    >
      <TableHeader>
        <TableRow>
          <TableHead>{t("data.colTenant")}</TableHead>
          <TableHead>{t("data.colStore")}</TableHead>
          <TableHead>{t("data.colSku")}</TableHead>
          <TableHead>{t("data.colTitle")}</TableHead>
          <TableHead>{t("data.colPrice")}</TableHead>
          <TableHead>{t("data.colCost")}</TableHead>
          <TableHead>{t("data.colActive")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.data?.items.map((p) => (
          <TableRow key={p.id}>
            <TableCell className="font-medium">{p.tenant_name ?? "—"}</TableCell>
            <TableCell className="text-xs">{p.store_domain ?? "—"}</TableCell>
            <TableCell className="font-mono text-xs">{p.sku}</TableCell>
            <TableCell className="max-w-72 truncate">{p.title}</TableCell>
            <TableCell className="tabular-nums">
              {p.price} {p.currency}
            </TableCell>
            <TableCell className="tabular-nums">
              {p.cost} {p.currency}
            </TableCell>
            <TableCell>
              {p.is_active ? (
                <Badge variant="success">{t("common.yes")}</Badge>
              ) : (
                <Badge variant="secondary">{t("common.no")}</Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TabShell>
  );
}

function OrdersTab({ tenantId }: { tenantId?: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["admin", "data", "orders", { tenantId, q, page }],
    queryFn: () =>
      adminSettingsApi.dataOrders({
        tenant_id: tenantId,
        q: q || undefined,
        page,
        page_size: PAGE_SIZE,
      }),
  });

  return (
    <TabShell
      searchValue={q}
      onSearch={(v) => {
        setQ(v);
        setPage(1);
      }}
      searchPlaceholder={t("data.searchOrders")}
      isLoading={list.isLoading}
      error={list.error ?? null}
      onRetry={() => list.refetch()}
      isEmpty={!!list.data && list.data.items.length === 0}
      emptyText={t("data.empty")}
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onPage={setPage}
        />
      }
    >
      <TableHeader>
        <TableRow>
          <TableHead>{t("data.colTenant")}</TableHead>
          <TableHead>{t("data.colOrderNo")}</TableHead>
          <TableHead>{t("data.colCustomer")}</TableHead>
          <TableHead>{t("data.colTotal")}</TableHead>
          <TableHead>{t("common.status")}</TableHead>
          <TableHead>{t("data.colPlacedAt")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.data?.items.map((o) => (
          <TableRow key={o.id}>
            <TableCell className="font-medium">{o.tenant_name ?? "—"}</TableCell>
            <TableCell className="font-mono text-xs">#{o.external_id}</TableCell>
            <TableCell className="text-xs">{o.customer_email_masked ?? "—"}</TableCell>
            <TableCell className="tabular-nums">
              {o.total} {o.currency}
            </TableCell>
            <TableCell>
              <Badge variant="outline">{o.status}</Badge>
            </TableCell>
            <TableCell className="text-xs whitespace-nowrap text-muted">
              {o.placed_at ? formatDateTime(o.placed_at) : formatDateTime(o.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TabShell>
  );
}

function CustomersTab({ tenantId }: { tenantId?: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["admin", "data", "customers", { tenantId, q, page }],
    queryFn: () =>
      adminSettingsApi.dataCustomers({
        tenant_id: tenantId,
        q: q || undefined,
        page,
        page_size: PAGE_SIZE,
      }),
  });

  return (
    <TabShell
      searchValue={q}
      onSearch={(v) => {
        setQ(v);
        setPage(1);
      }}
      searchPlaceholder={t("data.searchCustomers")}
      isLoading={list.isLoading}
      error={list.error ?? null}
      onRetry={() => list.refetch()}
      isEmpty={!!list.data && list.data.items.length === 0}
      emptyText={t("data.empty")}
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onPage={setPage}
        />
      }
    >
      <TableHeader>
        <TableRow>
          <TableHead>{t("data.colTenant")}</TableHead>
          <TableHead>{t("data.colCustomer")}</TableHead>
          <TableHead>{t("data.colOrders")}</TableHead>
          <TableHead>{t("data.colSpent")}</TableHead>
          <TableHead>{t("data.colFirstOrder")}</TableHead>
          <TableHead>{t("data.colLastOrder")}</TableHead>
          <TableHead>{t("data.colSilent")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.data?.items.map((c) => (
          <TableRow key={c.id}>
            <TableCell className="font-medium">{c.tenant_name ?? "—"}</TableCell>
            <TableCell className="text-xs">{c.email_masked ?? "—"}</TableCell>
            <TableCell className="tabular-nums">{c.total_orders}</TableCell>
            <TableCell className="tabular-nums">
              {c.total_spent} {c.currency}
            </TableCell>
            <TableCell className="text-xs whitespace-nowrap text-muted">
              {c.first_order_at ? formatDateTime(c.first_order_at) : "—"}
            </TableCell>
            <TableCell className="text-xs whitespace-nowrap text-muted">
              {c.last_order_at ? formatDateTime(c.last_order_at) : "—"}
            </TableCell>
            <TableCell>
              {c.is_silent ? (
                <Badge variant="warning">{t("common.yes")}</Badge>
              ) : (
                <Badge variant="secondary">{t("common.no")}</Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </TabShell>
  );
}

/** 四个 Tab 共用的骨架：搜索框 + 状态处理 + 卡片表格 + 分页。 */
function TabShell({
  searchValue,
  onSearch,
  searchPlaceholder,
  isLoading,
  error,
  onRetry,
  isEmpty,
  emptyText,
  pagination,
  children,
}: {
  searchValue: string;
  onSearch: (v: string) => void;
  searchPlaceholder: string;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  isEmpty: boolean;
  emptyText: string;
  pagination: React.ReactNode;
  children: React.ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Input
          value={searchValue}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-64"
        />
      </div>
      {isEmpty ? (
        <Card className="p-8 text-center text-sm text-muted">{emptyText}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>{children}</Table>
          {pagination}
        </Card>
      )}
    </div>
  );
}
